import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { CreateFlowcodeFormField } from '../../CreateFlowcodeForm.types'
import { FILE_UPLOAD_SIZE_ERROR, MAX_FILE_UPLOAD_SIZE } from '@app/code/src/constants/flowcode'
import {
  FileDestinations,
  LandingPageDestination,
  LandingPageDestinationType,
  SocialMediaDestinations
} from '@dtx-company/inter-app/src/types/flowcode'
import { FileInput } from '../../../../molecules/file-input'
import { Input } from '@dtx-company/shared-components/src/components/atoms/Input/index'
import { MAX_INSTAGRAM_INPUT_LENGTH } from '@app/common/src/constants/flowcode'
import { SOCIAL_MEDIA_DESTINATIONS } from '../../../../../constants/landing-page'
import { SimplifiedVariants } from '../Simplified.types'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import {
  VALIDATABLE_SOCIAL_MEDIA_DESTINATIONS,
  ValidatableSocialMediaDestination,
  useSocialHandleValidation
} from '@app/common/src/hooks/useSocialHandleValidation'
import { isLandingPageValid } from '../../../../../utils/landing-page'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { theme } from '@dtx-company/shared-components/src'
import { useCreateFlowcodeForm } from '../../CreateFlowcodeForm.hooks'
import { useFormState } from 'react-hook-form'
import { validFileTypes } from '../../../../../utils/flowcode'
import React, { ChangeEvent, useEffect, useState } from 'react'

export interface SimplifiedScanDestinationFormV1Props extends SimplifiedVariants {
  selectedDestination: LandingPageDestinationType
  customBorderRadius?: string
  onInputChange?: (destination: LandingPageDestinationType) => void
}

export const SimplifiedScanDestinationFormV1 = ({
  selectedDestination,
  customBorderRadius = '0 4px 4px 0',
  onInputChange
}: SimplifiedScanDestinationFormV1Props): JSX.Element => {
  const { watch, setValue, register, control, setIsDestinationValidating, clearErrors } =
    useCreateFlowcodeForm()
  const watchAll = watch()
  const { errors } = useFormState({ control })
  const [destinationInputEventFired, setDestinationInputEventFired] = useState(false)

  const handleInputEvent = (e: ChangeEvent<HTMLInputElement>): void => {
    clearErrors(CreateFlowcodeFormField.LINK)
    if (!destinationInputEventFired) {
      onInputChange?.(selectedDestination)
      setDestinationInputEventFired(true)
    }
  }

  const destination = selectedDestination.id
  const placeholder =
    selectedDestination.label === 'Website'
      ? 'Enter or paste a URL'
      : selectedDestination?.placeholder

  const { validateSocialHandle } = useSocialHandleValidation({
    setIsValidating: setIsDestinationValidating
  })

  useEffect(() => {
    register(CreateFlowcodeFormField.SocialMediaId)
  }, [register])

  if (destination in FileDestinations) {
    return (
      <Box display="block" width={'100%'} alignContent="center">
        <FileInput
          fileName={watchAll.file?.name || ''}
          label=""
          customStartAdornment={<></>}
          handleChange={e => {
            clearErrors(CreateFlowcodeFormField.File)
            const { files } = e.target
            const logoFile = files?.[0] || null
            if (logoFile && logoFile?.size > MAX_FILE_UPLOAD_SIZE) {
              return sendErrorNotification(FILE_UPLOAD_SIZE_ERROR.message)
            }
            setValue(CreateFlowcodeFormField.File, logoFile)
            handleInputEvent(e)
          }}
          placeholder={placeholder}
          maxSize={20}
          accept={validFileTypes(destination)}
          showUploadIconButton
          uploadIconBtnProps={{
            width: '30px',
            minWidth: '30px',
            height: '42px',
            margin: '0 0 2px 0',
            backgroundColor: 'secondary.highlight',
            color: 'primary.flowBlue',
            border: 'none'
          }}
          inputProps={{
            borderRadius: customBorderRadius,
            ...(!!errors.file && {
              border: `1px solid ${theme.colors.status.errorDark}`
            }),
            error: !!errors.file,
            helperText: errors.file?.message
          }}
          sharedInputProps={{
            'data-heap-id': 'SimplifiedScanDestinationFormV1_FileInput',
            ...register(CreateFlowcodeFormField.File, {
              validate: value => {
                if (!value) return 'Add a file'
                if (value?.size > MAX_FILE_UPLOAD_SIZE) return FILE_UPLOAD_SIZE_ERROR.message
                return true
              }
            })
          }}
          clearImage={() => setValue(CreateFlowcodeFormField.File, null)}
        />
      </Box>
    )
  }

  if (destination === LandingPageDestination.SMS) {
    const error = !!errors.sms?.phoneNumber
    return (
      <Input
        data-heap-id="SimplifiedScanDestinationFormV1_SMSInput"
        type="text"
        placeholder="555-555-5555"
        error={error}
        helperText={errors.sms?.phoneNumber?.message}
        inputProps={{
          ...register(CreateFlowcodeFormField.SMSPhoneNumber, {
            required: 'Add a phone number',
            onChange: handleInputEvent,
            shouldUnregister: true
          }),
          borderRadius: customBorderRadius,
          ...(error && {
            border: `1px solid ${theme.colors.status.errorDark}`
          })
        }}
      />
    )
  }

  if (destination in SocialMediaDestinations) {
    const socialSelected =
      SOCIAL_MEDIA_DESTINATIONS.find(d => d.id === destination) || SOCIAL_MEDIA_DESTINATIONS[0]
    const isValidatableDestination = VALIDATABLE_SOCIAL_MEDIA_DESTINATIONS.includes(
      socialSelected.id.toLocaleLowerCase()
    )
    const isFacebookLink = socialSelected?.id === LandingPageDestination.FACEBOOK
    const isUsernameLink = !!socialSelected?.prefix && !isFacebookLink

    const maxLength =
      destination === LandingPageDestination.INSTAGRAM ? MAX_INSTAGRAM_INPUT_LENGTH : undefined
    const error = !!errors[CreateFlowcodeFormField.LINK]

    return (
      <Input
        startAdornment={
          isUsernameLink ? (
            <Text pb="4px" ml="10px" color="primary.grey" fontWeight="500" width={'16px'}>
              @
            </Text>
          ) : undefined
        }
        type="text"
        placeholder={placeholder}
        error={error}
        helperText={errors[CreateFlowcodeFormField.LINK]?.message}
        {...(isFacebookLink && { maxWidth: 'unset' })}
        inputProps={{
          'data-heap-id': 'SimplifiedScanDestinationFormV1_SocialMediaInput',
          ...register(CreateFlowcodeFormField.LINK, {
            onChange: handleInputEvent,
            required: 'Add a scan destination',
            validate: async value => {
              if (!isValidatableDestination) return true
              return (
                (await validateSocialHandle({
                  handle: value,
                  platform:
                    socialSelected.id.toLocaleLowerCase() as ValidatableSocialMediaDestination
                })) || "Couldn't find matching page or profile"
              )
            },
            shouldUnregister: true
          }),
          borderRadius: customBorderRadius,
          ...(!isFacebookLink && { paddingLeft: '28px' }),
          ...(error && {
            border: `1px solid ${theme.colors.status.errorDark}`,
            spellCheck: false
          }),
          maxLength
        }}
      />
    )
  }

  if (destination === LandingPageDestination.EMAIL) {
    const error = !!errors.email?.email
    return (
      <Input
        type="email"
        placeholder="myemail@email.com"
        error={error}
        maxWidth="unset"
        helperText={errors.email?.email?.message}
        inputProps={{
          'data-heap-id': 'SimplifiedScanDestinationFormV1_EmailInput',
          ...register(CreateFlowcodeFormField.Email, {
            required: 'Add an email address',
            onChange: handleInputEvent,
            shouldUnregister: true
          }),
          autoCapitalize: 'off',
          ...(error && {
            border: `1px solid ${theme.colors.status.errorDark}`,
            spellCheck: false
          }),
          borderRadius: customBorderRadius
        }}
        marginBottom="12px"
        data-testid="email-form-address-input"
      />
    )
  }

  return (
    <Input
      type="text"
      placeholder={placeholder}
      error={!!errors[CreateFlowcodeFormField.LINK]}
      helperText={errors[CreateFlowcodeFormField.LINK]?.message}
      inputProps={{
        'data-heap-id': 'SimplifiedScanDestinationFormV1_LinkInput',
        ...register(CreateFlowcodeFormField.LINK, {
          required: 'Add a scan destination',
          onChange: e => {
            e.target.value = e.target.value.trim()
            handleInputEvent(e)
          },
          validate: (_value, formValues) => {
            return isLandingPageValid(destination, formValues) || 'Invalid URL'
          },
          shouldUnregister: true
        }),
        autoCapitalize: 'off',
        borderRadius: customBorderRadius,
        ...(!!errors[CreateFlowcodeFormField.LINK] && {
          border: `1px solid ${theme.colors.status.errorDark}`
        })
      }}
      data-testid={`scan-destination-input-${destination}`}
    />
  )
}
